import {
  TokenStorage,
  type TokenAdditionalInfo,
} from '@cimpress-technology/authfront-sdk-core'

import { setCookie, getCookie, deleteCookie } from './helper'

import {
  BASE_KEYS,
  NEW_KEYS,
  LEGACY_TENANTS,
  PLS_COOKIE_NAME,
  DEFAULT_USER_REFRESH_TOKEN_LIFETIME_IN_SEC,
  VP_CONFIG_DOMAIN
} from '../../common/constants'

export class CookieTokenStorage extends TokenStorage {
  private tenant: string = ''

  setTenant (tenant: string): void {
    this.tenant = tenant
  }

  // Legacy tenants (and a missing tenant) keep using the legacy keys; all other tenants use the new keys.
  private get keys (): typeof BASE_KEYS {
    return !this.tenant || LEGACY_TENANTS.includes(this.tenant) ? BASE_KEYS : NEW_KEYS
  }

  saveAccessToken (accessToken: string, expiresAt: Date, tokenType: string): void {
    setCookie(this.keys.REFERENCE_TOKEN_COOKIE_KEY, accessToken, expiresAt)
    setCookie(this.keys.REFERENCE_TOKEN_TYPE_COOKIE_KEY, tokenType, expiresAt)
  }

  retrieveAccessToken (): string | undefined {
    return getCookie(this.keys.REFERENCE_TOKEN_COOKIE_KEY)
  }

  deleteAccessToken (): void {
    deleteCookie(this.keys.REFERENCE_TOKEN_COOKIE_KEY)
    deleteCookie(this.keys.REFERENCE_TOKEN_TYPE_COOKIE_KEY)
  }

  saveRefreshToken (refreshToken: string, expiresAt: Date, saveAsCookie: boolean): void {
    setCookie(this.keys.REFRESH_TOKEN_COOKIE_KEY, JSON.stringify(refreshToken), expiresAt)
  }

  retrieveRefreshToken (): string | undefined {
    return getCookie(this.keys.REFRESH_TOKEN_COOKIE_KEY, true)
  }

  deleteRefreshToken (): void {
    deleteCookie(this.keys.REFRESH_TOKEN_COOKIE_KEY)
  }

  saveAdditionalInfo (payload: TokenAdditionalInfo): void {
    localStorage.setItem(this.getAdditionalInfoStorageKey(), JSON.stringify(payload))
  }

  retrieveAdditionalInfo (): TokenAdditionalInfo | undefined {
    const stored = localStorage.getItem(this.getAdditionalInfoStorageKey())
    return stored ? JSON.parse(stored) as TokenAdditionalInfo : undefined
  }

  deleteAdditionalInfo (): void {
    localStorage.removeItem(this.getAdditionalInfoStorageKey())
  }

  getAdditionalInfoStorageKey (): string {
    return this.keys.TOKEN_DETAILS_STORAGE_KEY
  }

  getCodeVerifier (): string {
    return getCookie(this.keys.CODE_VERIFIER) ?? ''
  }

  saveCodeVerifier (codeVerifier: string): void {
    setCookie(this.keys.CODE_VERIFIER, codeVerifier)
  }

  deleteCodeVerifier (): void {
    deleteCookie(this.keys.CODE_VERIFIER)
  }

  setPresumedLoginState (): void {
    const d = new Date()
    d.setTime(d.getTime() + DEFAULT_USER_REFRESH_TOKEN_LIFETIME_IN_SEC * 1000)
    setCookie(PLS_COOKIE_NAME, '1', d)
  }

  deletePresumedLoginState (): void {
    deleteCookie(PLS_COOKIE_NAME)
  }

  hasPresumedLoginState (): boolean {
    return getCookie(PLS_COOKIE_NAME) === '1'
  }

  setClientId (clientId: string): void {
    const d = new Date()
    d.setTime(d.getTime() + DEFAULT_USER_REFRESH_TOKEN_LIFETIME_IN_SEC * 1000)
    setCookie(this.keys.CONFIG, JSON.stringify({ clientId, domain: VP_CONFIG_DOMAIN }), d)
  }

  getClientId (): string | undefined {
    const config = getCookie(this.keys.CONFIG, true)
    return config?.clientId ?? undefined
  }

  deleteClientId (): void {
    deleteCookie(this.keys.CONFIG)
  }
}
